import React, { useEffect, useState, useMemo } from 'react';
import { useNavigate } from 'react-router-dom';
import { useAuth } from '../contexts/AuthContext';
import { useImpersonation } from '../contexts/ImpersonationContext';
import { useClientPortal } from '../contexts/ClientPortalContext';
import api from '../services/api';
import { clientPortfolioService, clientActionPlanService } from '../services';
import { Card, CardHeader, CardTitle, CardContent } from '../components/ui/card';
import { Button } from '../components/ui/button';
import { Input } from '../components/ui/input';
import { Badge } from '../components/ui/badge';
import {
  Dialog, DialogContent, DialogHeader, DialogTitle, DialogDescription,
} from '../components/ui/dialog';
import {
  Select, SelectContent, SelectItem, SelectTrigger, SelectValue,
} from '../components/ui/select';
import {
  TrendingUp, TrendingDown, Calendar, BarChart3, Search, Minus,
  CheckCircle2, AlertCircle, XCircle, Eye, Clock, Layers,
  ChevronLeft, ChevronRight, Download, ChevronDown, ArrowUpRight, ArrowDownRight,
  ClipboardList, ArrowRight, Loader2,
} from 'lucide-react';
import {
  BarChart, Bar, XAxis, YAxis, CartesianGrid, Tooltip, Legend, ResponsiveContainer,
} from 'recharts';

const ABBEY_GREEN = '#1a4d2e';
const ABBEY_GOLD = '#c9a84c';
const STATUS_COLORS = { green: '#22c55e', amber: '#f59e0b', red: '#ef4444', neutral: '#94a3b8' };
const PAGE_SIZE = 10;

const ClientPortfolioDashboardPage = () => {
  const { user } = useAuth();
  const navigate = useNavigate();
  const { isAnonymised, anonymise } = useImpersonation();
  const { apEnabled } = useClientPortal();
  const [loading, setLoading] = useState(true);
  const [portfolioData, setPortfolioData] = useState(null);
  const [upcomingAudits, setUpcomingAudits] = useState([]);
  const [comparisonData, setComparisonData] = useState(null);
  const [subRegions, setSubRegions] = useState([]);
  const [regionalTrends, setRegionalTrends] = useState(null);
  const [scopeFilter, setScopeFilter] = useState('');
  const [subRegionFilter, setSubRegionFilter] = useState('all');
  const [siteSearch, setSiteSearch] = useState('');
  const [tablePage, setTablePage] = useState(1);
  const [sortBy, setSortBy] = useState('site_name');
  const [sortDir, setSortDir] = useState('asc');
  const [error, setError] = useState(null);
  const [exporting, setExporting] = useState(false);
  const [collapsedRegions, setCollapsedRegions] = useState(new Set());
  const [groupByRegion, setGroupByRegion] = useState(true);
  const [kpiModal, setKpiModal] = useState(null); // { key, title }
  const [dashboardFeatures, setDashboardFeatures] = useState(null);
  const [apSummary, setApSummary] = useState(null);
  const [apLoading, setApLoading] = useState(true);

  // Action Plan oversight — only fetch when AP is enabled for this customer's scope.
  useEffect(() => {
    if (!apEnabled) return;
    let cancelled = false;
    setApLoading(true);
    const params = {};
    if (scopeFilter) params.scope = scopeFilter;
    const sr = subRegionFilter !== 'all' ? subRegions.find(s => String(s.id) === subRegionFilter) : null;
    if (sr?.site_ids?.length) params.site_ids = sr.site_ids.join(',');
    clientActionPlanService.analytics(params)
      .then(res => { if (!cancelled) setApSummary(res.summary); })
      .catch(() => { if (!cancelled) setApSummary(null); })
      .finally(() => { if (!cancelled) setApLoading(false); });
    return () => { cancelled = true; };
  }, [scopeFilter, subRegionFilter, subRegions, apEnabled]);

  // Fetch summary/comparison/sub-regions/trends (scope-dependent)
  useEffect(() => {
    const fetchData = async () => {
      setLoading(true);
      setError(null);
      try {
        const [summaryRes, comparisonRes, srRes, trendsRes, accessRes] = await Promise.all([
          clientPortfolioService.getPortfolioSummary(scopeFilter || null),
          clientPortfolioService.getSiteComparison(scopeFilter || null),
          clientPortfolioService.getSubRegions().catch(() => ({ sub_regions: [] })),
          clientPortfolioService.getRegionalTrends(scopeFilter || null, 6).catch(() => ({ periods: [], series: [] })),
          api.get('/client/me/access-summary').then(r => r.data).catch(() => null),
        ]);
        setPortfolioData(summaryRes);
        setComparisonData(comparisonRes);
        setSubRegions(srRes.sub_regions || []);
        setRegionalTrends(trendsRes);
        setDashboardFeatures(accessRes?.dashboard_features || null);
      } catch (err) {
        setError('Failed to load portfolio data. Please try again.');
      } finally { setLoading(false); }
    };
    fetchData();
  }, [scopeFilter]);

  // Fetch upcoming audits whenever sub-region filter changes (observation e)
  useEffect(() => {
    const fetchUpcoming = async () => {
      try {
        const res = await clientPortfolioService.getUpcomingAudits(
          10,
          subRegionFilter !== 'all' ? subRegionFilter : null
        );
        setUpcomingAudits(res);
      } catch {
        setUpcomingAudits([]);
      }
    };
    fetchUpcoming();
  }, [subRegionFilter]);

  const hasSubRegions = subRegions.length > 0;
  const isLargePortfolio = (portfolioData?.site_kpis?.length || 0) > 20;
  const activeSubRegion = subRegions.find(sr => String(sr.id) === subRegionFilter);

  // Feature-flag helper — backend resolves the auto-gate + admin overrides.
  // Falls back to legacy auto-gate logic if the access-summary call failed.
  const feat = (key) => {
    if (dashboardFeatures && Object.prototype.hasOwnProperty.call(dashboardFeatures, key)) {
      return !!dashboardFeatures[key];
    }
    // Fallback (matches catalogue auto_gate values)
    switch (key) {
      case 'regional_overview_cards':
      case 'regional_cycle_compare':
      case 'region_aggregated_chart':
        return isLargePortfolio && hasSubRegions;
      case 'top_bottom_cos':
        return isLargePortfolio;
      case 'top_bottom_stock_result_pct':
      case 'top_bottom_stock_result_amount':
      case 'kpi_stock_result_amount':
        return false;
      case 'group_by_region_toggle':
        return hasSubRegions;
      default:
        return true;
    }
  };

  // Filter sites by sub-region + search
  const filteredSites = useMemo(() => {
    if (!portfolioData?.site_kpis) return [];
    let sites = portfolioData.site_kpis;
    if (subRegionFilter !== 'all' && activeSubRegion) {
      const srSiteIds = new Set(activeSubRegion.site_ids || []);
      sites = sites.filter(s => srSiteIds.has(s.site_id));
    }
    if (siteSearch) {
      const q = siteSearch.toLowerCase();
      sites = sites.filter(s => s.site_name.toLowerCase().includes(q) || (s.group_name && s.group_name.toLowerCase().includes(q)));
    }
    return sites;
  }, [portfolioData, subRegionFilter, activeSubRegion, siteSearch]);

  const sortedSites = useMemo(() => {
    const sorted = [...filteredSites];
    sorted.sort((a, b) => {
      let va, vb;
      if (sortBy === 'site_name') { va = a.site_name || ''; vb = b.site_name || ''; return sortDir === 'asc' ? va.localeCompare(vb) : vb.localeCompare(va); }
      va = a[sortBy] ?? -Infinity; vb = b[sortBy] ?? -Infinity;
      return sortDir === 'asc' ? va - vb : vb - va;
    });
    return sorted;
  }, [filteredSites, sortBy, sortDir]);

  const pageCount = Math.ceil(sortedSites.length / PAGE_SIZE);
  const pagedSites = sortedSites.slice((tablePage - 1) * PAGE_SIZE, tablePage * PAGE_SIZE);

  // Aggregates for filtered view (only when a sub-region is active — override top cards)
  const filteredAggregates = useMemo(() => {
    if (subRegionFilter === 'all') return null;
    const sites = filteredSites.filter(s => s.has_data);
    if (sites.length === 0) return { total: filteredSites.length, with_data: 0 };
    const avg = (arr, key) => { const vals = arr.map(s => s[key]).filter(v => v != null); return vals.length ? (vals.reduce((a, b) => a + b, 0) / vals.length).toFixed(1) : null; };
    return {
      total: filteredSites.length, with_data: sites.length,
      avg_gp: avg(sites, 'gp_percent'), avg_cos: avg(sites, 'cos_percent'),
      avg_stock_result: avg(sites, 'stock_result_percent'),
      below_budget: sites.filter(s => s.cos_status === 'green').length,
      above_budget: sites.filter(s => s.cos_status === 'red').length,
    };
  }, [filteredSites, subRegionFilter]);

  // Chart data — site-level bars (used for sub-region view or small portfolios)
  const siteChartData = useMemo(() => {
    if (!comparisonData?.sites) return [];
    let sites = comparisonData.sites.filter(s => s.gp_percent !== null || s.cos_percent !== null);
    if (subRegionFilter !== 'all' && activeSubRegion) {
      const srIds = new Set(activeSubRegion.site_ids || []);
      sites = sites.filter(s => srIds.has(s.site_id));
    }
    return sites.map(s => {
      const aName = anonymise(s.site_name, 'site');
      return {
        name: aName.length > 18 ? aName.slice(0, 18) + '...' : aName,
        fullName: aName, site_id: s.site_id,
        gp_percent: s.gp_percent, cos_percent: s.cos_percent,
      };
    });
  }, [comparisonData, subRegionFilter, activeSubRegion]);

  // Aggregated-by-region chart data (observation a)
  const regionChartData = useMemo(() => {
    if (!hasSubRegions || !portfolioData?.site_kpis) return [];
    return subRegions.map(sr => {
      const srSiteIds = new Set(sr.site_ids || []);
      const sites = portfolioData.site_kpis.filter(s => srSiteIds.has(s.site_id) && s.has_data);
      if (sites.length === 0) return null;
      const avg = (key) => { const vals = sites.map(s => s[key]).filter(v => v != null); return vals.length ? Number((vals.reduce((a, b) => a + b, 0) / vals.length).toFixed(1)) : null; };
      const aName = anonymise(sr.name, 'region');
      return {
        id: sr.id,
        name: aName.length > 22 ? aName.slice(0, 22) + '...' : aName,
        fullName: aName,
        gp_percent: avg('gp_percent'),
        cos_percent: avg('cos_percent'),
        stock_result_percent: avg('stock_result_percent'),
        sites: sites.length,
      };
    }).filter(Boolean);
  }, [hasSubRegions, subRegions, portfolioData]);

  // Top/Bottom 5 sites ranked by COS% only (lowest = best, highest = worst).
  // Stock Result % and £ Result are shown as additional columns alongside but
  // do not drive ranking — keeps the panel a familiar 2-table layout while
  // exposing the extra metrics inline.
  const topBottom = useMemo(() => {
    const cosSites = filteredSites.filter(s => s.cos_percent != null);
    return {
      cos: {
        top:    [...cosSites].sort((a, b) => a.cos_percent - b.cos_percent).slice(0, 5),
        bottom: [...cosSites].sort((a, b) => b.cos_percent - a.cos_percent).slice(0, 5),
      },
    };
  }, [filteredSites]);

  // Legacy aliases kept for any references below
  const topPerformers = topBottom.cos.top;
  const bottomPerformers = topBottom.cos.bottom;

  // Sub-region summary cards
  const subRegionSummaries = useMemo(() => {
    if (!hasSubRegions || !portfolioData?.site_kpis) return [];
    return subRegions.map(sr => {
      const srSiteIds = new Set(sr.site_ids || []);
      const sites = portfolioData.site_kpis.filter(s => srSiteIds.has(s.site_id));
      const withData = sites.filter(s => s.has_data);
      const avgCos = withData.length ? (withData.reduce((sum, s) => sum + (s.cos_percent || 0), 0) / withData.length).toFixed(1) : null;
      const avgResultSites = withData.filter(s => s.stock_result_percent != null);
      const avgResult = avgResultSites.length ? (avgResultSites.reduce((sum, s) => sum + s.stock_result_percent, 0) / avgResultSites.length).toFixed(1) : null;
      const belowBudget = withData.filter(s => s.cos_status === 'green').length;
      return { ...sr, sites_total: sites.length, sites_with_data: withData.length, avg_cos: avgCos, avg_result: avgResult, below_budget: belowBudget, above_budget: withData.filter(s => s.cos_status === 'red').length };
    }).filter(sr => sr.sites_total > 0);
  }, [hasSubRegions, subRegions, portfolioData]);

  // Regional cycle compare data — latest vs previous COS % per region (observation 2 fix)
  const regionCycleCompareData = useMemo(() => {
    if (!regionalTrends?.series?.length || !regionalTrends.periods?.length) return [];
    const periods = regionalTrends.periods;
    return regionalTrends.series.map(s => {
      // Find the latest non-null and the previous non-null value
      let latest = null, previous = null;
      for (let i = s.cos.length - 1; i >= 0; i--) {
        if (s.cos[i] != null) {
          if (latest === null) latest = s.cos[i];
          else if (previous === null) { previous = s.cos[i]; break; }
        }
      }
      if (latest === null && previous === null) return null;
      return {
        id: s.sub_region_id,
        name: anonymise(s.name, 'region'),
        latest: latest,
        previous: previous,
      };
    }).filter(Boolean);
  }, [regionalTrends]);

  const StatusIcon = ({ status }) => {
    if (status === 'green') return <CheckCircle2 className="h-4 w-4" style={{ color: STATUS_COLORS.green }} />;
    if (status === 'amber') return <AlertCircle className="h-4 w-4" style={{ color: STATUS_COLORS.amber }} />;
    if (status === 'red') return <XCircle className="h-4 w-4" style={{ color: STATUS_COLORS.red }} />;
    return <Minus className="h-4 w-4" style={{ color: STATUS_COLORS.neutral }} />;
  };

  const getKpiCellStyle = (status) => {
    if (status === 'green') return { backgroundColor: '#dcfce7', color: '#166534' };
    if (status === 'amber') return { backgroundColor: '#fef3c7', color: '#92400e' };
    if (status === 'red') return { backgroundColor: '#fee2e2', color: '#991b1b' };
    return {};
  };

  const formatDate = (d) => d ? new Date(d).toLocaleDateString('en-GB', { day: '2-digit', month: 'short', year: 'numeric' }) : '-';
  const toggleSort = (col) => { if (sortBy === col) setSortDir(d => d === 'asc' ? 'desc' : 'asc'); else { setSortBy(col); setSortDir('asc'); } setTablePage(1); };
  const SortArrow = ({ col }) => sortBy === col ? <span className="ml-1">{sortDir === 'asc' ? '\u25B2' : '\u25BC'}</span> : null;
  const toggleRegionCollapse = (regionId) => setCollapsedRegions(prev => { const n = new Set(prev); n.has(regionId) ? n.delete(regionId) : n.add(regionId); return n; });

  const handleExportCSV = async () => {
    setExporting(true);
    try {
      const blob = await clientPortfolioService.exportCSV(scopeFilter || null, subRegionFilter !== 'all' ? subRegionFilter : null);
      const url = window.URL.createObjectURL(new Blob([blob]));
      const link = document.createElement('a');
      link.href = url;
      link.setAttribute('download', `portfolio_report_${activeSubRegion?.name?.replace(/\s/g, '_') || 'all_sites'}.csv`);
      document.body.appendChild(link);
      link.click();
      link.remove();
      window.URL.revokeObjectURL(url);
    } catch { /* ignore */ } finally { setExporting(false); }
  };

  // Group sites by sub-region for collapsible display
  const groupedSitesByRegion = useMemo(() => {
    if (!hasSubRegions || subRegionFilter !== 'all' || !groupByRegion) return null;
    const groups = [];
    const assignedSiteIds = new Set();
    for (const sr of subRegions) {
      const srSiteIds = new Set(sr.site_ids || []);
      const regionSites = sortedSites.filter(s => srSiteIds.has(s.site_id));
      if (regionSites.length > 0) {
        groups.push({ id: sr.id, name: anonymise(sr.name, 'region'), sites: regionSites });
        regionSites.forEach(s => assignedSiteIds.add(s.site_id));
      }
    }
    const unassigned = sortedSites.filter(s => !assignedSiteIds.has(s.site_id));
    if (unassigned.length > 0) groups.push({ id: 'unassigned', name: 'Other Sites', sites: unassigned });
    return groups;
  }, [hasSubRegions, subRegionFilter, groupByRegion, subRegions, sortedSites]);

  const CustomBarTooltip = ({ active, payload }) => {
    if (!active || !payload?.length) return null;
    const data = payload[0]?.payload;
    return (
      <div className="bg-white border border-slate-200 rounded-lg shadow-lg p-3 text-sm">
        <p className="font-semibold text-slate-900 mb-1">{data?.fullName || data?.name}</p>
        {data?.sites != null && <p className="text-xs text-slate-500 mb-1">{data.sites} sites</p>}
        {payload.map((entry, i) => (
          <p key={i} style={{ color: entry.color }} className="flex items-center gap-2">
            <span className="w-3 h-3 rounded-sm" style={{ backgroundColor: entry.color }} />
            {entry.dataKey === 'gp_percent' ? 'GP %' : entry.dataKey === 'cos_percent' ? 'COS %' : entry.dataKey === 'stock_result_percent' ? 'Result %' : entry.dataKey}: {entry.value?.toFixed(1)}%
          </p>
        ))}
      </div>
    );
  };

  if (loading) return (
    <div className="flex items-center justify-center min-h-[60vh]">
      <div className="text-center"><div className="animate-spin rounded-full h-12 w-12 border-b-2 border-emerald-600 mx-auto" /><p className="mt-4 text-sm text-muted-foreground">Loading portfolio data...</p></div>
    </div>
  );
  if (error) return <div className="p-6"><Card><CardContent className="py-8 text-center text-red-600">{error}</CardContent></Card></div>;

  const overview = portfolioData?.overview;
  // Use filtered aggregates when sub-region active, else full portfolio aggregates
  const aggregates = filteredAggregates || portfolioData?.aggregates;
  const prevAggregates = subRegionFilter === 'all' ? portfolioData?.previous_aggregates : null;

  // Comparator delta helper (current vs previous). Returns { delta, isPositive } or null.
  const buildDelta = (curr, prev, inverseGood = false) => {
    if (curr == null || prev == null) return null;
    const d = Number((curr - prev).toFixed(1));
    if (d === 0) return { delta: 0, good: null };
    const good = inverseGood ? d < 0 : d > 0;
    return { delta: d, good };
  };

  const ComparatorBadge = ({ delta, suffix = 'pp', invertColor = false }) => {
    if (!delta) return null;
    if (delta.delta === 0) return <span className="text-[10px] text-slate-400 ml-1">0</span>;
    const positive = delta.delta > 0;
    const color = delta.good === null ? 'text-slate-400' : delta.good ? 'text-green-600' : 'text-red-600';
    const Icon = positive ? ArrowUpRight : ArrowDownRight;
    return (
      <span className={`text-[10px] font-medium ${color} inline-flex items-center ml-1`}>
        <Icon className="h-2.5 w-2.5" />{Math.abs(delta.delta)}{suffix}
      </span>
    );
  };

  // Number of sub-region summary cards (for dynamic grid width — observation d)
  const srCardCount = subRegionSummaries.length;
  const srGridCols = srCardCount === 1 ? 'grid-cols-1'
    : srCardCount === 2 ? 'grid-cols-1 sm:grid-cols-2'
    : srCardCount === 3 ? 'grid-cols-1 sm:grid-cols-2 lg:grid-cols-3'
    : 'grid-cols-1 sm:grid-cols-2 lg:grid-cols-3 xl:grid-cols-4';

  // KPI tile config (for modals + comparators)
  const kpiTiles = [
    {
      key: 'sites', label: 'Sites',
      value: aggregates?.total || overview?.total_sites || 0,
      color: ABBEY_GREEN, delta: null,
      sublabel: subRegionFilter !== 'all' ? anonymise(activeSubRegion?.name, 'region') : null,
    },
    {
      key: 'avg_gp', label: 'Avg GP %',
      value: `${aggregates?.avg_gp ?? aggregates?.avg_gp_percent ?? '-'}%`,
      color: ABBEY_GREEN,
      delta: buildDelta(aggregates?.avg_gp ?? aggregates?.avg_gp_percent, prevAggregates?.avg_gp_percent, false),
    },
    {
      key: 'avg_cos', label: 'Avg COS %',
      value: `${aggregates?.avg_cos ?? aggregates?.avg_cos_percent ?? '-'}%`,
      color: ABBEY_GREEN,
      delta: buildDelta(aggregates?.avg_cos ?? aggregates?.avg_cos_percent, prevAggregates?.avg_cos_percent, true),
    },
    {
      key: 'below_budget', label: 'Below Budget',
      value: aggregates?.below_budget ?? aggregates?.sites_below_budget ?? 0,
      color: STATUS_COLORS.green,
      delta: buildDelta(aggregates?.below_budget ?? aggregates?.sites_below_budget, prevAggregates?.sites_below_budget, false),
      suffix: '',
    },
    {
      key: 'above_budget', label: 'Above Budget',
      value: aggregates?.above_budget ?? aggregates?.sites_above_budget ?? 0,
      color: STATUS_COLORS.red,
      delta: buildDelta(aggregates?.above_budget ?? aggregates?.sites_above_budget, prevAggregates?.sites_above_budget, true),
      suffix: '',
    },
    {
      key: 'avg_stock_result', label: 'Avg Stock Result',
      value: `${aggregates?.avg_stock_result ?? aggregates?.avg_stock_result_percent ?? '-'}%`,
      color: (() => { const v = aggregates?.avg_stock_result ?? aggregates?.avg_stock_result_percent; return v != null && parseFloat(v) >= -2 && parseFloat(v) <= 0.5 ? STATUS_COLORS.green : STATUS_COLORS.red; })(),
      delta: buildDelta(aggregates?.avg_stock_result ?? aggregates?.avg_stock_result_percent, prevAggregates?.avg_stock_result_percent, false),
    },
  ];

  // Build site list for a given KPI modal (sorted by the relevant metric)
  const getKpiModalSites = (key) => {
    const src = filteredSites.filter(s => s.has_data);
    switch (key) {
      case 'sites':
        // All sites, by name
        return [...filteredSites].sort((a, b) => a.site_name.localeCompare(b.site_name));
      case 'avg_gp':
        // Highest GP% first (best performers top)
        return [...src].sort((a, b) => (b.gp_percent ?? -Infinity) - (a.gp_percent ?? -Infinity));
      case 'avg_cos':
        // Highest COS% first (biggest contributors to cost top)
        return [...src].sort((a, b) => (b.cos_percent ?? -Infinity) - (a.cos_percent ?? -Infinity));
      case 'below_budget':
        // Sites below budget, sorted by how far below (most below first)
        return src.filter(s => s.cos_status === 'green')
          .sort((a, b) => (a.cos_percent ?? Infinity) - (b.cos_percent ?? Infinity));
      case 'above_budget':
        // Sites above budget, sorted by how far above (most above first)
        return src.filter(s => s.cos_status === 'red')
          .sort((a, b) => (b.cos_percent ?? -Infinity) - (a.cos_percent ?? -Infinity));
      case 'avg_stock_result':
        // Biggest absolute stock result deviation first
        return [...src.filter(s => s.stock_result_percent != null)]
          .sort((a, b) => Math.abs(b.stock_result_percent) - Math.abs(a.stock_result_percent));
      default:
        return src;
    }
  };

  return (
    <div className="space-y-6" data-testid="portfolio-dashboard">
      {/* Header with filters — region filter always present (observation f) */}
      <div className="flex items-center justify-between flex-wrap gap-3 sticky top-0 z-20 bg-background pt-1 pb-3 -mx-4 px-4 border-b border-slate-100">
        <div>
          <h1 className="text-2xl font-bold" style={{ color: ABBEY_GREEN }}>Portfolio Dashboard</h1>
          <p className="text-sm text-muted-foreground">
            {overview?.total_sites || 0} sites across portfolio
            {subRegionFilter !== 'all' && activeSubRegion && <span className="ml-2 text-violet-700 font-medium">• {activeSubRegion.name} ({filteredSites.length} sites)</span>}
          </p>
        </div>
        <div className="flex items-center gap-2 flex-wrap">
          {hasSubRegions && (
            <Select value={subRegionFilter} onValueChange={v => { setSubRegionFilter(v); setTablePage(1); }}>
              <SelectTrigger className="w-[200px] h-9" data-testid="sub-region-filter">
                <Layers className="h-3.5 w-3.5 mr-1.5 text-violet-600" />
                <SelectValue placeholder="All Regions" />
              </SelectTrigger>
              <SelectContent>
                <SelectItem value="all">All Regions</SelectItem>
                {subRegions.map(sr => <SelectItem key={sr.id} value={String(sr.id)}>{anonymise(sr.name, 'region')} ({sr.site_count})</SelectItem>)}
              </SelectContent>
            </Select>
          )}
          <Select value={scopeFilter || 'ALL'} onValueChange={v => setScopeFilter(v === 'ALL' ? '' : v)}>
            <SelectTrigger className="w-[130px] h-9" data-testid="scope-filter"><SelectValue placeholder="All Scopes" /></SelectTrigger>
            <SelectContent>
              <SelectItem value="ALL">All Scopes</SelectItem>
              <SelectItem value="BEVERAGE">Beverage</SelectItem>
              <SelectItem value="FOOD">Food</SelectItem>
            </SelectContent>
          </Select>
          <Button
            size="sm" variant="outline" className="h-9" onClick={handleExportCSV}
            disabled={exporting || isAnonymised}
            title={isAnonymised ? 'CSV export disabled in Anonymised Demo Mode' : undefined}
            data-testid="export-csv-btn"
          >
            <Download className="h-3.5 w-3.5 mr-1.5" />
            {exporting ? 'Exporting...' : 'Export CSV'}
          </Button>
        </div>
      </div>

      {/* Sub-Region Summary Cards — dynamic width based on count (observation d) */}
      {hasSubRegions && feat('regional_overview_cards') && subRegionFilter === 'all' && (
        <div data-testid="sub-region-cards">
          <h2 className="text-sm font-semibold text-slate-600 mb-2 flex items-center gap-1.5">
            <Layers className="h-4 w-4 text-violet-600" />Regional Overview
          </h2>
          <div className={`grid ${srGridCols} gap-3`}>
            {subRegionSummaries.map(sr => (
              <Card key={sr.id} className="cursor-pointer hover:border-violet-300 hover:shadow-md transition-all"
                onClick={() => setSubRegionFilter(String(sr.id))} data-testid={`sub-region-card-${sr.id}`}>
                <CardContent className="pt-4 pb-4">
                  <div className="flex items-center justify-between mb-3">
                    <p className="font-semibold text-base truncate">{anonymise(sr.name, 'region')}</p>
                    <Badge variant="outline" className="text-[10px]">{sr.sites_total} sites</Badge>
                  </div>
                  <div className="grid grid-cols-3 gap-3 text-center">
                    <div>
                      <p className="text-xs text-muted-foreground">COS %</p>
                      <p className="text-2xl font-bold" style={{ color: sr.avg_cos ? ABBEY_GREEN : STATUS_COLORS.neutral }}>{sr.avg_cos ?? '-'}%</p>
                    </div>
                    <div>
                      <p className="text-xs text-muted-foreground">Result %</p>
                      <p className="text-2xl font-bold" style={{ color: sr.avg_result && parseFloat(sr.avg_result) >= -2 && parseFloat(sr.avg_result) <= 0.5 ? STATUS_COLORS.green : STATUS_COLORS.red }}>{sr.avg_result ?? '-'}%</p>
                    </div>
                    <div>
                      <p className="text-xs text-muted-foreground">On Target</p>
                      <p className="text-2xl font-bold" style={{ color: STATUS_COLORS.green }}>{sr.below_budget}/{sr.sites_with_data}</p>
                    </div>
                  </div>
                </CardContent>
              </Card>
            ))}
          </div>
        </div>
      )}

      {/* KPI Summary Cards — clickable with comparators (observation c) */}
      <div className="grid grid-cols-2 md:grid-cols-3 lg:grid-cols-6 gap-3" data-testid="kpi-cards">
        {kpiTiles.map(tile => (
          <Card key={tile.key}
            className="cursor-pointer hover:shadow-md hover:border-emerald-200 transition-all"
            onClick={() => setKpiModal({ key: tile.key, title: tile.label })}
            data-testid={`kpi-tile-${tile.key}`}>
            <CardContent className="pt-4 pb-3">
              <p className="text-xs text-muted-foreground flex items-center justify-between">
                {tile.label}
                {tile.delta && prevAggregates && <ComparatorBadge delta={tile.delta} suffix={tile.suffix ?? 'pp'} />}
              </p>
              <p className="text-2xl font-bold" style={{ color: tile.color }}>{tile.value}</p>
              {tile.sublabel && <p className="text-[10px] text-violet-600 truncate">{tile.sublabel}</p>}
            </CardContent>
          </Card>
        ))}
      </div>

      {/* Action Plan Oversight — reuses the canonical client AP analytics
          endpoint; identical outstanding/overdue/awaiting-verification
          definitions as the Analytics screen. Cards drill into Analytics
          with scope/breadcrumb/back state retained (?state=&from=dashboard),
          carrying the active region/service filter through via site_ids/
          scope (server-side intersected with true access — never trusted
          blindly). */}
      {/* Action Plan Oversight — only shown when AP is enabled for this customer's scope. */}
      {apEnabled === true && feat('action_plan_oversight') && (
        <Card data-testid="ap-oversight-section">
          <CardHeader className="pb-2">
            <CardTitle className="text-base font-medium flex items-center gap-2">
              <ClipboardList className="h-4 w-4" style={{ color: ABBEY_GREEN }} />
              Action Plan Oversight
              {subRegionFilter !== 'all' && activeSubRegion && (
                <span className="text-xs font-normal text-violet-600 ml-1" data-testid="ap-oversight-region-label">
                  — {anonymise(activeSubRegion.name, 'region')}
                </span>
              )}
            </CardTitle>
          </CardHeader>
          <CardContent>
            {apLoading ? (
              <div className="flex justify-center py-6 text-slate-400"><Loader2 className="h-5 w-5 animate-spin" /></div>
            ) : !apSummary ? (
              <p className="text-sm text-muted-foreground text-center py-4">Action plan data unavailable.</p>
            ) : (
              <>
                <div className="grid grid-cols-2 md:grid-cols-5 gap-3">
                  {[
                    {
                      key: 'outstanding', label: 'Outstanding', value: apSummary.outstanding,
                      sub: apSummary.original_age_days_median != null
                        ? `Median age: ${Math.round(apSummary.original_age_days_median)}d`
                        : 'Median age: Unknown',
                    },
                    { key: 'overdue', label: 'Overdue', value: apSummary.overdue, highlight: apSummary.overdue > 0 },
                    { key: 'awaiting_verification', label: 'Pending Verification', value: apSummary.awaiting_verification },
                    { key: 'verified', label: 'Verified Closure', value: apSummary.verified_closed },
                    { key: null, label: 'Verified (30d)', value: apSummary.verified_closed_last_30d, sub: 'Throughput, not a rate' },
                  ].map(tile => {
                    const regionQS = (subRegionFilter !== 'all' && activeSubRegion?.site_ids?.length)
                      ? `&site_ids=${activeSubRegion.site_ids.join(',')}&region_label=${encodeURIComponent(anonymise(activeSubRegion.name, 'region'))}`
                      : '';
                    const scopeQS = scopeFilter ? `&scope=${scopeFilter}` : '';
                    return (
                      <div
                        key={tile.label}
                        onClick={tile.key ? () => navigate(`/client/action-plans/analytics?state=${tile.key}&from=dashboard${regionQS}${scopeQS}`) : undefined}
                        className={`rounded-lg border p-3 flex flex-col gap-0.5 ${tile.highlight ? 'border-red-300 bg-red-50' : 'border-slate-200 bg-white'} ${tile.key ? 'cursor-pointer hover:border-emerald-300 transition-colors' : ''}`}
                        data-testid={`ap-oversight-tile-${tile.key || 'verified-30d'}`}
                      >
                        <span className="text-xs text-muted-foreground">{tile.label}</span>
                        <span className="text-xl font-bold" style={{ color: tile.highlight ? STATUS_COLORS.red : ABBEY_GREEN }}>{tile.value ?? '—'}</span>
                        {tile.sub && <span className="text-[10px] text-muted-foreground">{tile.sub}</span>}
                      </div>
                    );
                  })}
                </div>
                {/* TRUTHFULNESS — this is the CURRENT cohort's latest known
                    state by month, not a full historical event ledger (a
                    re-assessed action only shows its latest verdict/date).
                    If there is no usable date evidence at all, say so
                    explicitly rather than showing a misleading all-zero
                    chart. */}
                {apSummary.raised_vs_verified_trend_coverage &&
                 !apSummary.raised_vs_verified_trend_coverage.has_raised_history &&
                 !apSummary.raised_vs_verified_trend_coverage.has_verified_history ? (
                  <p className="text-xs text-slate-400 mt-4" data-testid="ap-oversight-trend-insufficient">
                    Insufficient recorded history to show a raised/verified trend for this scope.
                  </p>
                ) : apSummary.raised_vs_verified_trend?.length > 0 && (
                  <div className="mt-4">
                    <p className="text-xs font-semibold text-slate-600 mb-0.5">Raised vs. Verified — Last 6 Months</p>
                    <p className="text-[10px] text-slate-400 mb-1">
                      Current known state only — reflects each action's latest recorded verdict/date, not a full historical log of every re-assessment.
                    </p>
                    <ResponsiveContainer width="100%" height={160}>
                      <BarChart data={apSummary.raised_vs_verified_trend} margin={{ top: 5, right: 10, left: 0, bottom: 5 }}>
                        <CartesianGrid strokeDasharray="3 3" stroke="#e2e8f0" />
                        <XAxis dataKey="label" tick={{ fontSize: 10 }} />
                        <YAxis tick={{ fontSize: 10 }} allowDecimals={false} />
                        <Tooltip />
                        <Legend wrapperStyle={{ fontSize: 10 }} />
                        <Bar dataKey="raised" name="Raised" fill="#94a3b8" radius={[2, 2, 0, 0]} />
                        <Bar dataKey="verified" name="Verified" fill={ABBEY_GREEN} radius={[2, 2, 0, 0]} />
                      </BarChart>
                    </ResponsiveContainer>
                  </div>
                )}
                <div className="flex justify-end mt-2">
                  <Button
                    size="sm" variant="ghost" className="h-7 text-xs"
                    onClick={() => {
                      const regionQS = (subRegionFilter !== 'all' && activeSubRegion?.site_ids?.length)
                        ? `&site_ids=${activeSubRegion.site_ids.join(',')}&region_label=${encodeURIComponent(anonymise(activeSubRegion.name, 'region'))}`
                        : '';
                      const scopeQS = scopeFilter ? `&scope=${scopeFilter}` : '';
                      navigate(`/client/action-plans/analytics?from=dashboard${regionQS}${scopeQS}`);
                    }}
                    data-testid="ap-oversight-view-all-btn"
                  >
                    View full analytics <ArrowRight className="h-3 w-3 ml-1" />
                  </Button>
                </div>
              </>
            )}
          </CardContent>
        </Card>
      )}

      {/* Regional Comparison — current vs previous cycle (diverging bars) */}
      {hasSubRegions && feat('regional_cycle_compare') && subRegionFilter === 'all' && regionCycleCompareData.length > 0 && (
        <Card data-testid="regional-cycle-compare-chart">
          <CardHeader className="pb-2">
            <CardTitle className="text-base font-medium flex items-center gap-2">
              <TrendingUp className="h-4 w-4" style={{ color: ABBEY_GREEN }} />
              Regional COS % — Latest vs. Previous Audit Cycle
            </CardTitle>
          </CardHeader>
          <CardContent>
            <ResponsiveContainer width="100%" height={Math.max(240, 60 + regionCycleCompareData.length * 46)}>
              <BarChart data={regionCycleCompareData} layout="vertical" margin={{ top: 5, right: 30, left: 20, bottom: 5 }}>
                <CartesianGrid strokeDasharray="3 3" stroke="#e2e8f0" />
                <XAxis type="number" tick={{ fontSize: 11 }} tickFormatter={v => `${v}%`} />
                <YAxis type="category" dataKey="name" tick={{ fontSize: 11 }} width={150} />
                <Tooltip formatter={(val) => val != null ? `${val}%` : '-'} />
                <Legend wrapperStyle={{ fontSize: 11 }} />
                <Bar dataKey="previous" name="Previous Cycle" fill={ABBEY_GOLD} radius={[0, 3, 3, 0]} />
                <Bar dataKey="latest" name="Latest Cycle" fill={ABBEY_GREEN} radius={[0, 3, 3, 0]} />
              </BarChart>
            </ResponsiveContainer>
          </CardContent>
        </Card>
      )}

      {/* Main comparison chart — aggregated-by-region for large portfolios, site-level otherwise (observation a) */}
      {(() => {
        const showRegionAgg = hasSubRegions && feat('region_aggregated_chart') && subRegionFilter === 'all' && regionChartData.length > 0;
        const dataToShow = showRegionAgg ? regionChartData : siteChartData;
        const title = showRegionAgg
          ? 'Regional Comparison — Avg GP % & COS %'
          : `Site GP % & COS % Comparison${activeSubRegion ? ` — ${anonymise(activeSubRegion.name, 'region')}` : ''}`;
        const emptyMsg = showRegionAgg ? 'No regional data available' : 'No data available';
        return (
          <Card data-testid="comparison-chart">
            <CardHeader className="pb-2">
              <CardTitle className="text-base font-medium flex items-center justify-between">
                <span>{title}</span>
                {showRegionAgg && <span className="text-xs font-normal text-muted-foreground">Click a bar to drill into region</span>}
              </CardTitle>
            </CardHeader>
            <CardContent>
              {dataToShow.length > 0 ? (
                <ResponsiveContainer width="100%" height={showRegionAgg ? 340 : Math.max(320, 140 + dataToShow.length * 18)}>
                  <BarChart data={dataToShow} margin={{ top: 10, right: 20, left: 10, bottom: showRegionAgg ? 30 : 90 }}>
                    <CartesianGrid strokeDasharray="3 3" stroke="#e2e8f0" />
                    <XAxis dataKey="name" tick={{ fontSize: showRegionAgg ? 11 : 10 }} angle={showRegionAgg ? 0 : -40} textAnchor={showRegionAgg ? 'middle' : 'end'} height={showRegionAgg ? 40 : 90} interval={0} />
                    <YAxis tick={{ fontSize: 11 }} tickFormatter={v => `${v}%`} />
                    <Tooltip content={<CustomBarTooltip />} />
                    <Legend wrapperStyle={{ fontSize: 11 }} />
                    <Bar dataKey="gp_percent" name="GP %" fill={ABBEY_GREEN} radius={[3, 3, 0, 0]}
                      onClick={showRegionAgg ? (d) => d?.id && setSubRegionFilter(String(d.id)) : undefined}
                      style={showRegionAgg ? { cursor: 'pointer' } : undefined} />
                    <Bar dataKey="cos_percent" name="COS %" fill={ABBEY_GOLD} radius={[3, 3, 0, 0]}
                      onClick={showRegionAgg ? (d) => d?.id && setSubRegionFilter(String(d.id)) : undefined}
                      style={showRegionAgg ? { cursor: 'pointer' } : undefined} />
                  </BarChart>
                </ResponsiveContainer>
              ) : (
                <div className="text-center py-8 text-muted-foreground"><BarChart3 className="h-10 w-10 mx-auto opacity-30 mb-2" /><p className="text-sm">{emptyMsg}</p></div>
              )}
            </CardContent>
          </Card>
        );
      })()}

      {/* Top/Bottom performers — single pair of tables ranked by COS%,
          with Stock Result % and £ Result columns alongside as info.
          Rows are clickable into the site's full property dashboard.
          (2026-04-27 user feedback: revert iter320 side-by-side, keep
          the simple 2-table view + the two extra columns only.)
          iter417 — Hidden for Food audits to mirror the Stocktake
          Wizard, which suppresses Top/Bottom Performers for Food
          (COS%/Stock-Result metrics aren't computed for Food and the
          panel previously rendered empty rows). */}
      {scopeFilter !== 'FOOD' && (() => {
        const showCos    = feat('top_bottom_cos');
        const showSrPct  = feat('top_bottom_stock_result_pct');
        const showSrAmt  = feat('top_bottom_stock_result_amount');
        if (!showCos && !showSrPct && !showSrAmt) return null;
        // If COS% is gated off but at least one extra column is on, still
        // render with COS column shown but unranked? We always rank by COS
        // for stability; if COS data is missing, drop the row.

        const fmtPct = (v) => v == null ? '—' : `${v.toFixed(1)}%`;
        const fmtSignedPct = (v) => v == null ? '—' : `${v >= 0 ? '+' : ''}${v.toFixed(1)}%`;
        const fmtGBP = (v) => v == null ? '—'
          : new Intl.NumberFormat('en-GB', { style: 'currency', currency: 'GBP', maximumFractionDigits: 0 }).format(v);
        const srPctTone = (v) => v == null ? 'text-slate-500' : v >= -0.5 ? 'text-emerald-700' : 'text-amber-700';
        const srAmtTone = (v) => v == null ? 'text-slate-500' : v >= 0 ? 'text-emerald-700' : 'text-rose-700';

        const Table = ({ rows, kind }) => (
          <div className="rounded-md border border-slate-200 overflow-hidden">
            <div className={`px-3 py-1.5 text-[11px] font-semibold flex items-center gap-1.5 border-b border-slate-200 ${kind === 'top' ? 'bg-emerald-50 text-emerald-800' : 'bg-rose-50 text-rose-800'}`}>
              {kind === 'top' ? <TrendingDown className="h-3 w-3" /> : <TrendingUp className="h-3 w-3" />}
              {kind === 'top' ? 'Top 5 — Lowest COS %' : 'Bottom 5 — Highest COS %'}
            </div>
            <table className="w-full text-xs">
              <thead className="bg-slate-50 text-slate-500">
                <tr>
                  <th className="px-2 py-1.5 w-6 text-left">#</th>
                  <th className="px-1 py-1.5 text-left">Site</th>
                  {showCos   && <th className="px-1 py-1.5 text-right whitespace-nowrap w-14">COS %</th>}
                  {showSrPct && <th className="px-1 py-1.5 text-right whitespace-nowrap w-16">Result %</th>}
                  {showSrAmt && <th className="px-2 py-1.5 text-right whitespace-nowrap w-20">£ Result</th>}
                </tr>
              </thead>
              <tbody>
                {rows.length === 0 ? (
                  <tr><td colSpan={2 + (showCos?1:0) + (showSrPct?1:0) + (showSrAmt?1:0)} className="p-3 text-center text-muted-foreground">No data</td></tr>
                ) : rows.map((s, i) => (
                  <tr key={s.site_id}
                      className="border-t border-slate-100 hover:bg-slate-50 cursor-pointer transition-colors"
                      onClick={() => navigate(`/client/property-dashboard?site=${s.site_id}`)}
                      data-testid={`performers-${kind}-row-${s.site_id}`}>
                    <td className="px-2 py-1.5 text-slate-400 font-bold">{i + 1}</td>
                    <td className="px-1 py-1.5 truncate text-slate-900 hover:underline" title={anonymise(s.site_name, 'site')}>{anonymise(s.site_name, 'site')}</td>
                    {showCos   && <td className={`px-1 py-1.5 text-right font-semibold whitespace-nowrap w-14 ${kind === 'top' ? 'text-emerald-700' : 'text-rose-700'}`}>{fmtPct(s.cos_percent)}</td>}
                    {showSrPct && <td className={`px-1 py-1.5 text-right whitespace-nowrap w-16 ${srPctTone(s.stock_result_percent)}`}>{fmtSignedPct(s.stock_result_percent)}</td>}
                    {showSrAmt && <td className={`px-2 py-1.5 text-right whitespace-nowrap w-20 ${srAmtTone(s.stock_result_value)}`}>{fmtGBP(s.stock_result_value)}</td>}
                  </tr>
                ))}
              </tbody>
            </table>
          </div>
        );

        return (
          <Card data-testid="performers-section">
            <CardHeader className="pb-2">
              <CardTitle className="text-base font-medium">
                Top / Bottom Performers{activeSubRegion ? ` — ${anonymise(activeSubRegion.name, 'region')}` : ''}
                <span className="ml-2 text-xs font-normal text-muted-foreground">ranked by COS %</span>
              </CardTitle>
            </CardHeader>
            <CardContent>
              <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
                <Table rows={topBottom.cos.top}    kind="top" />
                <Table rows={topBottom.cos.bottom} kind="bottom" />
              </div>
              <p className="text-[10px] text-muted-foreground mt-2 text-right">Click any site to open its full report</p>
            </CardContent>
          </Card>
        );
      })()}

      {/* Paginated Site Performance Table */}
      <Card data-testid="site-performance-table">
        <CardHeader className="pb-2">
          <div className="flex items-center justify-between flex-wrap gap-2">
            <CardTitle className="text-base font-medium">
              Site Performance{activeSubRegion ? ` — ${anonymise(activeSubRegion.name, 'region')}` : ''}
              <span className="text-xs font-normal text-muted-foreground ml-2">({sortedSites.length} sites)</span>
            </CardTitle>
            <div className="flex items-center gap-2">
              {hasSubRegions && feat('group_by_region_toggle') && subRegionFilter === 'all' && (
                <Button size="sm" variant={groupByRegion ? 'default' : 'outline'} className="h-8 text-xs"
                  onClick={() => setGroupByRegion(p => !p)} data-testid="group-by-region-btn">
                  <Layers className="h-3 w-3 mr-1" />{groupByRegion ? 'Grouped' : 'Flat'}
                </Button>
              )}
              <div className="relative">
                <Search className="absolute left-2.5 top-1/2 -translate-y-1/2 h-3.5 w-3.5 text-slate-400" />
                <Input placeholder="Search sites..." value={siteSearch} onChange={e => { setSiteSearch(e.target.value); setTablePage(1); }} className="pl-8 w-[180px] h-8 text-xs" data-testid="site-search" />
              </div>
            </div>
          </div>
        </CardHeader>
        <CardContent>
          <div className="overflow-x-auto">
            <table className="w-full text-sm">
              <thead>
                <tr className="border-b border-slate-200">
                  <th className="text-left py-2 px-2 font-medium text-slate-600 cursor-pointer select-none" onClick={() => toggleSort('site_name')}>Site<SortArrow col="site_name" /></th>
                  <th className="text-center py-2 px-2 font-medium text-slate-600 cursor-pointer select-none" onClick={() => toggleSort('latest_audit_date')}>Last Audit<SortArrow col="latest_audit_date" /></th>
                  <th className="text-center py-2 px-2 font-medium text-slate-600 cursor-pointer select-none" onClick={() => toggleSort('cos_percent')}>COS %<SortArrow col="cos_percent" /></th>
                  <th className="text-center py-2 px-2 font-medium text-slate-600 cursor-pointer select-none" onClick={() => toggleSort('stock_result_percent')}>Stock Result %<SortArrow col="stock_result_percent" /></th>
                  <th className="text-center py-2 px-2 font-medium text-slate-600 cursor-pointer select-none" onClick={() => toggleSort('stockholding_days')}>Days<SortArrow col="stockholding_days" /></th>
                  <th className="text-center py-2 px-2 font-medium text-slate-600">Actions</th>
                </tr>
              </thead>
              <tbody>
                {groupedSitesByRegion ? (
                  groupedSitesByRegion.map(group => {
                    const isCollapsed = collapsedRegions.has(group.id);
                    return (
                      <React.Fragment key={group.id}>
                        <tr className="bg-slate-50 cursor-pointer hover:bg-slate-100" onClick={() => toggleRegionCollapse(group.id)} data-testid={`region-group-${group.id}`}>
                          <td colSpan={6} className="py-2 px-2">
                            <div className="flex items-center gap-2">
                              {isCollapsed ? <ChevronRight className="h-3.5 w-3.5 text-slate-400" /> : <ChevronDown className="h-3.5 w-3.5 text-slate-400" />}
                              <Layers className="h-3.5 w-3.5 text-violet-500" />
                            <span className="font-semibold text-sm text-slate-700">{anonymise(group.name, 'region')}</span>
                              <Badge variant="secondary" className="text-[10px]">{group.sites.length} sites</Badge>
                            </div>
                          </td>
                        </tr>
                        {!isCollapsed && group.sites.map(site => (
                          <tr key={site.site_id} className="border-b border-slate-100 hover:bg-slate-50" data-testid={`site-row-${site.site_id}`}>
                            <td className="py-2 px-2 pl-8"><p className="font-medium text-slate-900 text-sm">{anonymise(site.site_name, 'site')}</p></td>
                            <td className="py-2 px-2 text-center text-xs">{site.has_data ? formatDate(site.latest_audit_date) : '-'}</td>
                            <td className="py-2 px-2 text-center">
                              {site.cos_percent != null ? (
                                <span className="inline-flex items-center gap-1 px-2 py-0.5 rounded text-xs font-medium" style={getKpiCellStyle(site.cos_status)}>
                                  <StatusIcon status={site.cos_status} /> {site.cos_percent.toFixed(1)}%
                                </span>
                              ) : '-'}
                            </td>
                            <td className="py-2 px-2 text-center">
                              {site.stock_result_percent != null ? (
                                <span className="text-xs font-medium" style={getKpiCellStyle(site.stock_result_status)}> {site.stock_result_percent.toFixed(1)}%</span>
                              ) : '-'}
                            </td>
                            <td className="py-2 px-2 text-center text-xs">{site.stockholding_days != null ? `${Math.round(site.stockholding_days)} Days` : '-'}</td>
                            <td className="py-2 px-2 text-center">
                              <Button size="sm" variant="ghost" className="h-7 text-xs" onClick={() => navigate(`/client/property-dashboard?site=${site.site_id}`)}>
                                <Eye className="h-3 w-3 mr-1" />View
                              </Button>
                            </td>
                          </tr>
                        ))}
                      </React.Fragment>
                    );
                  })
                ) : (
                  pagedSites.length === 0 ? (
                    <tr><td colSpan={6} className="py-6 text-center text-muted-foreground text-xs">{siteSearch ? 'No sites match' : 'No data'}</td></tr>
                  ) : pagedSites.map(site => (
                    <tr key={site.site_id} className="border-b border-slate-100 hover:bg-slate-50" data-testid={`site-row-${site.site_id}`}>
                      <td className="py-2 px-2"><p className="font-medium text-slate-900 text-sm">{anonymise(site.site_name, 'site')}</p></td>
                      <td className="py-2 px-2 text-center text-xs">{site.has_data ? formatDate(site.latest_audit_date) : '-'}</td>
                      <td className="py-2 px-2 text-center">
                        {site.cos_percent != null ? (
                          <span className="inline-flex items-center gap-1 px-2 py-0.5 rounded text-xs font-medium" style={getKpiCellStyle(site.cos_status)}>
                            <StatusIcon status={site.cos_status} /> {site.cos_percent.toFixed(1)}%
                          </span>
                        ) : '-'}
                      </td>
                      <td className="py-2 px-2 text-center">
                        {site.stock_result_percent != null ? (
                          <span className="text-xs font-medium" style={getKpiCellStyle(site.stock_result_status)}> {site.stock_result_percent.toFixed(1)}%</span>
                        ) : '-'}
                      </td>
                      <td className="py-2 px-2 text-center text-xs">{site.stockholding_days != null ? `${Math.round(site.stockholding_days)} Days` : '-'}</td>
                      <td className="py-2 px-2 text-center">
                        <Button size="sm" variant="ghost" className="h-7 text-xs" onClick={() => navigate(`/client/property-dashboard?site=${site.site_id}`)}>
                          <Eye className="h-3 w-3 mr-1" />View
                        </Button>
                      </td>
                    </tr>
                  ))
                )}
              </tbody>
            </table>
          </div>
          {!groupedSitesByRegion && pageCount > 1 && (
            <div className="flex items-center justify-between mt-3 pt-2 border-t">
              <span className="text-xs text-muted-foreground">Page {tablePage} of {pageCount}</span>
              <div className="flex gap-1">
                <Button size="sm" variant="outline" className="h-7 w-7 p-0" disabled={tablePage <= 1} onClick={() => setTablePage(p => p - 1)}>
                  <ChevronLeft className="h-3.5 w-3.5" />
                </Button>
                <Button size="sm" variant="outline" className="h-7 w-7 p-0" disabled={tablePage >= pageCount} onClick={() => setTablePage(p => p + 1)}>
                  <ChevronRight className="h-3.5 w-3.5" />
                </Button>
              </div>
            </div>
          )}
        </CardContent>
      </Card>

      {/* Upcoming Audits — now region-aware (observation e) */}
      <Card data-testid="upcoming-audits">
        <CardHeader className="pb-2">
          <CardTitle className="text-base font-medium flex items-center gap-2">
            <Calendar className="h-4 w-4" style={{ color: ABBEY_GREEN }} />
            Upcoming Audits
            {activeSubRegion && <Badge variant="outline" className="text-[10px] ml-1">{anonymise(activeSubRegion.name, 'region')}</Badge>}
            <span className="text-xs font-normal text-muted-foreground ml-auto">{upcomingAudits.length} scheduled</span>
          </CardTitle>
        </CardHeader>
        <CardContent>
          {upcomingAudits.length === 0 ? (
            <p className="text-xs text-muted-foreground text-center py-3">No upcoming audits{activeSubRegion ? ` in ${anonymise(activeSubRegion.name, 'region')}` : ''}</p>
          ) : (
            <div className="space-y-2">
              {upcomingAudits.map((audit, idx) => (
                <div key={idx} className="flex items-center justify-between py-2 border-b border-slate-100 last:border-0">
                  <div>
                    <p className="text-sm font-medium">{anonymise(audit.site_name, 'site')}</p>
                    <p className="text-xs text-muted-foreground">{audit.scope || 'Beverage'}</p>
                  </div>
                  <div className="flex items-center gap-2">
                    <Clock className="h-3.5 w-3.5 text-slate-400" />
                    <span className="text-sm">{formatDate(audit.scheduled_date)}</span>
                  </div>
                </div>
              ))}
            </div>
          )}
        </CardContent>
      </Card>

      {/* KPI Breakdown Modal (observation c) */}
      <Dialog open={!!kpiModal} onOpenChange={(open) => { if (!open) setKpiModal(null); }}>
        <DialogContent className="max-w-2xl max-h-[80vh] overflow-hidden flex flex-col" data-testid="kpi-breakdown-dialog">
          <DialogHeader>
            <DialogTitle>
              {kpiModal?.title} Breakdown
              {kpiModal && <Badge variant="outline" className="ml-2 text-xs">{anonymise(activeSubRegion?.name, 'region')}</Badge>}
            </DialogTitle>
            <DialogDescription className="text-xs">
              {(() => {
                const sortLabels = {
                  sites: 'Sorted alphabetically',
                  avg_gp: 'Sorted by GP % (highest first)',
                  avg_cos: 'Sorted by COS % (highest first)',
                  below_budget: 'Sorted by COS % (lowest first)',
                  above_budget: 'Sorted by COS % (highest first)',
                  avg_stock_result: 'Sorted by absolute deviation (largest first)',
                };
                return (sortLabels[kpiModal?.key] || 'Sites contributing to this metric') + (prevAggregates ? ' — vs. previous period.' : '.');
              })()}
            </DialogDescription>
          </DialogHeader>
          <div className="overflow-y-auto flex-1">
            <table className="w-full text-sm">
              <thead className="sticky top-0 bg-white border-b">
                <tr>
                  <th className="text-left py-2 px-2 font-medium text-slate-600">Site</th>
                  <th className="text-center py-2 px-2 font-medium text-slate-600">COS %</th>
                  <th className="text-center py-2 px-2 font-medium text-slate-600">Result %</th>
                  <th className="text-center py-2 px-2 font-medium text-slate-600">Last Audit</th>
                  <th className="text-center py-2 px-2 font-medium text-slate-600">Action</th>
                </tr>
              </thead>
              <tbody>
                {kpiModal && getKpiModalSites(kpiModal.key).map(site => (
                  <tr key={site.site_id} className="border-b border-slate-100 hover:bg-slate-50">
                    <td className="py-2 px-2 font-medium">{anonymise(site.site_name, 'site')}</td>
                    <td className="py-2 px-2 text-center">
                      {site.cos_percent != null ? (
                        <span className="inline-flex items-center gap-1 px-2 py-0.5 rounded text-xs font-medium" style={getKpiCellStyle(site.cos_status)}>
                          <StatusIcon status={site.cos_status} />{site.cos_percent.toFixed(1)}%
                        </span>
                      ) : '-'}
                    </td>
                    <td className="py-2 px-2 text-center">
                      {site.stock_result_percent != null ? (
                        <span className="text-xs font-medium" style={getKpiCellStyle(site.stock_result_status)}>{site.stock_result_percent.toFixed(1)}%</span>
                      ) : '-'}
                    </td>
                    <td className="py-2 px-2 text-center text-xs">{site.has_data ? formatDate(site.latest_audit_date) : '-'}</td>
                    <td className="py-2 px-2 text-center">
                      <Button size="sm" variant="ghost" className="h-7 text-xs"
                        onClick={() => { setKpiModal(null); navigate(`/client/property-dashboard?site=${site.site_id}`); }}>
                        <Eye className="h-3 w-3 mr-1" />View
                      </Button>
                    </td>
                  </tr>
                ))}
                {kpiModal && getKpiModalSites(kpiModal.key).length === 0 && (
                  <tr><td colSpan={5} className="py-6 text-center text-muted-foreground text-xs">No sites match</td></tr>
                )}
              </tbody>
            </table>
          </div>
        </DialogContent>
      </Dialog>
    </div>
  );
};

export default ClientPortfolioDashboardPage;
